import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Models } from "@/api/types";
import { Text } from "@/atoms";
import { PlatformLogo } from "@/components/PlatformLogo";
import { useAppState } from "@/contexts/AppContext";
import { GiftCardPreviewImage } from "@/modules/giftCards";
import MobileViewportContainer from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import {
  ProductSlider,
  ProductSliderItem,
} from "@/modules/referralLanding/components/ProductSlider";
import { LocalStorageService } from "@/modules/storage";
import { useUser } from "@/modules/user/useUser/useUser";
import { ThemeContext } from "@/pages/Root";
import { Button } from "atoms/button/Button";

type GiftCardClaimContainerProps = {
  platform: Models<"PlatformModel">;
  transfer: Models<"DisplayGiftCardTransferAnonymous">;
  topListings: Models<"ListingModel">[];
};

export const GiftCardClaimContainer = ({
  platform,
  transfer,
  topListings,
}: GiftCardClaimContainerProps) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { resetTheme } = useContext(ThemeContext);
  const appState = useAppState();

  const isAuthenticated = !!user && !user.isGuest;

  const handleClaimItemUnauthenticated = () => {
    console.log(
      "[ClaimGiftCard] Unauthenticated claim - redirecting to signin"
    );
    // Data is already stored in localStorage and theme is already applied via useEffect
    // Navigate to V3 signin/signup flow for unauthenticated users
    navigate("/signin");
  };

  const handleClaimItemAuthenticated = () => {
    console.log("[ClaimGiftCard] Authenticated user - navigating to home");
    console.log(
      "[ClaimGiftCard] Home page will handle the gift card claim modal"
    );

    // For authenticated users, just navigate to home
    // The home page already has a modal that automatically claims unclaimed gift cards
    navigate("/home");
  };

  const handleClaimItem = isAuthenticated
    ? handleClaimItemAuthenticated
    : handleClaimItemUnauthenticated;

  // Type assertion for properties not yet in TypeScript types
  const transferData =
    transfer as Models<"DisplayGiftCardTransferAnonymous"> & {
      recipientPhoneNumber?: string;
      recipientPlatformUserId?: string;
      locationCity?: string;
      locationState?: string;
      locationZipCode?: string;
    };

  // Store gift card transfer data in localStorage on page load
  // so it's available whether they click a service card or the main button
  // Also set platform ID in app state to apply the correct theme immediately
  useEffect(() => {
    if (transfer.giftCardTransferId) {
      LocalStorageService.setItem(
        "giftCardTransferId",
        transfer.giftCardTransferId
      );
    }

    if (transferData.recipientPhoneNumber) {
      LocalStorageService.setItem(
        "giftCardRecipientPhone",
        transferData.recipientPhoneNumber
      );
    }

    if (transferData.recipientPlatformUserId) {
      LocalStorageService.setItem(
        "giftCardRecipientPlatformUserId",
        transferData.recipientPlatformUserId
      );
    }

    // Store gift card visual data for the redeem animation
    if (transfer.giftCardListingMediaItemUri) {
      LocalStorageService.setItem(
        "giftCardImageUrl",
        transfer.giftCardListingMediaItemUri
      );
    }

    // Store gift card sender name for signup source pre-filling
    if (transfer.senderFirstName) {
      LocalStorageService.setItem(
        "referralSenderName",
        transfer.senderFirstName
      );
    }

    LocalStorageService.setItem(
      "giftCardAmount",
      transfer.giftCardItemAmountInCents.toString()
    );
    LocalStorageService.setItem("isGiftCardClaim", "true");

    // Set platform ID in app state to apply platform-specific theme
    if (transfer.platformId) {
      appState.setPlatformId(transfer.platformId);
      // Reset theme to load platform-specific styling
      resetTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transfer.platformId,
    transfer.giftCardTransferId,
    transfer.giftCardListingMediaItemUri,
    transfer.giftCardItemAmountInCents,
    transfer.senderFirstName,
    transfer.recipientPhoneNumber,
    transfer.recipientPlatformUserId,
  ]);

  const amountInDollars = transfer.giftCardItemAmountInCents / 100;

  const getHeaderText = () => {
    return `${transfer.senderFirstName} sent you a $${amountInDollars} gift card!`;
  };

  const getSubheaderText = () => {
    if (transfer.personalMessage) {
      return transfer.personalMessage;
    }
    return `Enjoy your gift card at ${
      transfer.locationName || platform?.name || "our location"
    }.`;
  };

  const headerText = getHeaderText();
  const subheaderText = getSubheaderText();

  const renderGiftCard = () => {
    return (
      <div className="flex flex-col items-center gap-4 pb-2 pt-6">
        <div className="h-[200px] w-full max-w-[300px] overflow-hidden rounded-xl shadow-md">
          <GiftCardPreviewImage
            backgroundImage={transfer.giftCardListingMediaItemUri ?? ""}
            amount={amountInDollars.toString()}
            size="large"
            platformLogoUrl={platform?.logoImageUrl}
          />
        </div>
      </div>
    );
  };

  const renderTopServices = () => {
    return (
      <>
        <div className="flex flex-col items-center gap-4 py-6">
          <Text
            variant="body1"
            className="text-center text-secondary"
            text="Our top selling services:"
          />
        </div>

        {topListings.length === 1 ? (
          <button
            className="flex justify-center"
            onClick={() => handleClaimItem()}
          >
            <ProductSliderItem listing={topListings[0]} />
          </button>
        ) : (
          <ProductSlider
            listings={topListings || []}
            onItemClick={handleClaimItem}
          />
        )}
      </>
    );
  };

  return (
    <MobileViewportContainer
      bottomCta={
        <div className="mb-4 flex flex-col items-center gap-4 px-6">
          <Button
            style="primary"
            size="large"
            fullWidth
            onClick={handleClaimItem}
          >
            Claim it now
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <PlatformLogo
          logoUrl={platform?.logoImageUrl}
          platformName={platform?.name}
          className="mb-9 max-w-40"
        />
        <Text
          variant="v3-h1"
          className="text-center text-primary"
          text={headerText}
        />
        <Text
          variant="body2"
          className="text-center text-secondary"
          text={subheaderText}
        />
      </div>

      {renderGiftCard()}
      {renderTopServices()}
    </MobileViewportContainer>
  );
};
